import React, { useEffect, useState } from 'react';
import { ref, onValue } from 'firebase/database';
import { db } from '../utils/firebase';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';

const Spinner = () => (
  <div className="flex min-h-[40vh] items-center justify-center">
    <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-700 border-t-brand-500" />
  </div>
);

const CustomerEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isFr = language === 'fr';
  const t = (frText, enText) => (isFr ? frText : enText);

  useEffect(() => {
    const eventsRef = ref(db, 'events');
    const unsubscribe = onValue(
      eventsRef,
      (snapshot) => {
        const data = snapshot.val() || {};
        const list = Object.entries(data)
          .map(([id, ev]) => ({ id, ...ev }))
          .sort((a, b) => {
            const da = new Date(a.dateTime || a.date || 0).getTime();
            const db_ = new Date(b.dateTime || b.date || 0).getTime();
            return da - db_;
          });
        setEvents(list);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading events:', error);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 bg-mesh-gradient">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 bg-mesh-gradient pb-20">
      <header className="sticky top-0 z-10 border-b border-slate-800/80 bg-slate-950/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <div>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="mb-2 text-xs font-medium text-slate-500 transition hover:text-brand-400"
            >
              {t('← Accueil', '← Home')}
            </button>
            <h1 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">{t('Événements à venir', 'Upcoming events')}</h1>
            <p className="mt-1 text-sm text-slate-500">{t('Diffusions et premières prévues au programme', 'Broadcasts and premieres from the schedule')}</p>
          </div>
          <LanguageSwitcher />
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {events.map((ev) => (
            <motion.article
              key={ev.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="ui-card overflow-hidden p-0"
            >
              {ev.imageUrl ? (
                <div className="aspect-[16/10] w-full overflow-hidden bg-slate-900">
                  <img src={ev.imageUrl} alt="" className="h-full w-full object-cover" />
                </div>
              ) : (
                <div className="flex aspect-[16/10] items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900">
                  <span className="text-sm font-medium text-slate-600">{t('Aucune image', 'No image')}</span>
                </div>
              )}
              <div className="p-5">
                <h2 className="text-lg font-semibold text-white">{ev.title}</h2>
                <p className="mt-2 text-xs font-medium uppercase tracking-wider text-brand-400/90">
                  {format(new Date(ev.dateTime || ev.date || Date.now()), 'd MMM yyyy · HH:mm', isFr ? { locale: fr } : undefined)}
                </p>
                {ev.description ? (
                  <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-slate-500">{ev.description}</p>
                ) : null}
              </div>
            </motion.article>
          ))}
        </div>
        {events.length === 0 ? (
          <div className="ui-panel py-16 text-center">
            <p className="font-medium text-slate-300">{t('Aucun événement publié pour le moment', 'No events published yet')}</p>
            <p className="mt-2 text-sm text-slate-500">{t('Revenez plus tard pour de nouvelles annonces.', 'Check back later for new listings.')}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CustomerEvents;
